// MobileDetail — tela de DETALHE da superfície MOBILE (touch). Parede A5.
//
// VOD e série. Tap num card (Home/abas/Minha lista) abre ESTA tela, não o
// player direto — o padrão touch é detalhe → Assistir. Reusa a MESMA camada de
// dados das detail screens da TV: api.getVodInfo (VOD) e api.getSeriesInfo
// (série + temporadas/episódios). Keys scopadas via useProviderQueryKey.
//
// VOD: backdrop/poster, título, ano, gênero, sinopse, botão Assistir (→player,
//   retoma da posição salva) e favoritar (local-db, mesma store da TV).
// Série: + seletor de temporada + lista de episódios. Tap no episódio → player
//   com season/ep (destrava salvar posição de série). Botão principal
//   "Continuar/Assistir" abre o episódio em que o usuário PAROU (progresso
//   salvo, getPositionsForSeries) — retoma, avança pro próximo após terminar,
//   ou cai no 1º quando não há progresso (pickSeriesContinue, igual à TV). A
//   lista marca episódios assistidos (✓) e em andamento (barra de progresso).
//
// no_provider: CTA "configure fonte" (não crash). A apresentação é touch; foco
// de remoto / Norigin NÃO entram aqui.

import { useTranslation } from '@app-iptv/i18n'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { api, ApiError } from '../api'
import { Poster, rewriteImgUrl } from '../components/Poster'
import { useProviderQueryKey } from '../hooks/useScopedQueryKey'
import { cssUrl } from '../lib/css-url'
import { toggleFavoriteOptimistic } from '../lib/optimistic-favorite'
import {
  favoriteId,
  getPosition,
  getPositionsForSeries,
  isFavorite,
} from '../local-db'

import {
  buildEpisodeTarget,
  buildVodTarget,
  flattenSeasons,
  pickSeriesContinue,
  seasonKeysOf,
  type EpisodeProgress,
  type FlatEpisode,
} from './mobile-catalog'
import type { MobilePlayerTarget } from './mobile-shell-view'
import styles from './MobileDetail.module.css'
import { MobileLoading, MobileNoSource } from './MobileStates'

function isNoProvider(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'no_provider'
}

export interface MobileDetailProps {
  id: string
  kind: 'vod' | 'series'
  /** Título/poster do card pra pintar imediato (evita "piscar"). */
  title?: string | undefined
  poster?: string | null | undefined
  /** Abrir o player com o alvo resolvido (VOD ou episódio de série). */
  onPlay: (target: MobilePlayerTarget) => void
  onConfigureSource?: (() => void) | undefined
}

export function MobileDetail({
  id,
  kind,
  title,
  poster,
  onPlay,
  onConfigureSource,
}: MobileDetailProps): JSX.Element {
  const { t } = useTranslation()
  const [favorited, setFavorited] = useState(false)
  const [resumeFrom, setResumeFrom] = useState(0)

  // ===== Dados do conteúdo (mesmas queries que as detail screens da TV) ===== //
  const vodQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-detail-vod', id]),
    queryFn: () => api.getVodInfo(id),
    enabled: kind === 'vod' && Boolean(id),
    staleTime: 10 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })
  const seriesQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-detail-series', id]),
    queryFn: () => api.getSeriesInfo(id),
    enabled: kind === 'series' && Boolean(id),
    staleTime: 10 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  // ===== Favorito (local-db, mesma store da TV) ===== //
  const favId = favoriteId(kind === 'series' ? 'series' : 'vod', id)
  useEffect(() => {
    let alive = true
    void isFavorite(favId).then((v) => {
      if (alive) setFavorited(v)
    })
    return () => {
      alive = false
    }
  }, [favId])

  // ===== Posição salva (continuar) — só VOD nesta tela ===== //
  useEffect(() => {
    if (kind !== 'vod') return
    let alive = true
    void getPosition(`vod:${id}`).then((p) => {
      if (alive && p && !p.finished) setResumeFrom(Math.floor(p.positionSec))
    })
    return () => {
      alive = false
    }
  }, [id, kind])

  // ===== Progresso por episódio (continuar de série) ===== //
  // Mapa episodeId → progresso salvo. Alimenta o botão principal (continuar do
  // ep em que parou, não sempre o 1º) e a marca visual de progresso na lista.
  // Mesma fonte da SeriesDetailsScreen da TV (getPositionsForSeries). Recarrega
  // ao voltar do player (visibilitychange/focus) pra a marca não ficar stale.
  const [epProgress, setEpProgress] = useState<Record<string, EpisodeProgress>>({})
  useEffect(() => {
    if (kind !== 'series' || !id) return
    let alive = true
    const load = (): void => {
      void getPositionsForSeries(id).then((list) => {
        if (!alive) return
        const map: Record<string, EpisodeProgress> = {}
        for (const p of list) {
          if (!p.episodeId) continue
          map[p.episodeId] = {
            episodeId: p.episodeId,
            positionSec: p.positionSec,
            durationSec: p.durationSec,
            lastWatchedAt: p.lastWatchedAt,
            finished: p.finished,
          }
        }
        setEpProgress(map)
      })
    }
    load()
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', load)
    return () => {
      alive = false
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', load)
    }
  }, [id, kind])

  // ===== Série: temporadas + episódios ===== //
  const seasons = seriesQ.data?.seasons
  const seasonKeys = useMemo(() => seasonKeysOf(seasons), [seasons])
  // Temporada escolhida pelo usuário (null = nenhuma escolha ainda).
  const [pickedSeason, setPickedSeason] = useState<string | null>(null)
  // Temporada EFETIVA, derivada no render (não via effect): respeita a escolha
  // do usuário quando válida, senão cai na 1ª. Assim os episódios aparecem já
  // na 1ª render (sem "piscar" vazio até um useEffect rodar).
  const activeSeason =
    pickedSeason && seasonKeys.includes(pickedSeason) ? pickedSeason : seasonKeys[0] ?? null

  const episodes = useMemo<FlatEpisode[]>(() => {
    if (!seasons || !activeSeason) return []
    return flattenSeasons({ [activeSeason]: seasons[activeSeason] ?? [] })
  }, [seasons, activeSeason])

  // Todos os episódios (todas as temporadas, ordem cronológica) — base pra
  // decidir o "continuar" da série (o ep mais recente pode estar em outra
  // temporada que não a ativa).
  const allEpisodes = useMemo<FlatEpisode[]>(() => flattenSeasons(seasons), [seasons])

  // Episódio que o botão principal deve abrir (continuar de onde parou, próximo
  // após terminar, ou 1º quando não há progresso). null enquanto não há eps.
  const seriesContinue = useMemo(
    () => (kind === 'series' ? pickSeriesContinue(allEpisodes, epProgress) : null),
    [kind, allEpisodes, epProgress],
  )

  const seriesMeta = seriesQ.data?.series

  // Metadados unificados pra o cabeçalho (VOD ou série), com fallback do card.
  const displayTitle =
    (kind === 'vod' ? vodQ.data?.name : seriesMeta?.name) ?? title ?? ''
  const displayPoster =
    (kind === 'vod' ? vodQ.data?.poster : seriesMeta?.poster) ?? poster ?? null
  const backdrop =
    (kind === 'vod' ? vodQ.data?.backdrop : seriesMeta?.backdrop) ?? displayPoster
  const plot = kind === 'vod' ? vodQ.data?.plot : seriesMeta?.plot
  const year = kind === 'vod' ? vodQ.data?.year : seriesMeta?.year
  const genre = kind === 'vod' ? vodQ.data?.genre : seriesMeta?.genre

  const toggleFavorite = useCallback(() => {
    // Estrela otimista com reversão — ver lib/optimistic-favorite.ts.
    void toggleFavoriteOptimistic({
      favorited,
      favId,
      paint: setFavorited,
      makeInput: () => ({
        id: favId,
        type: kind === 'series' ? 'series' : 'vod',
        itemId: id,
        addedAt: Math.floor(Date.now() / 1000),
        title: displayTitle,
        poster: displayPoster,
      }),
    })
  }, [favorited, favId, kind, id, displayTitle, displayPoster])

  const playVod = useCallback(() => {
    onPlay(buildVodTarget(id, { title: displayTitle, poster: displayPoster, resumeFrom }))
  }, [onPlay, id, displayTitle, displayPoster, resumeFrom])

  const playEpisode = useCallback(
    (ep: FlatEpisode, resume = 0) => {
      onPlay(
        buildEpisodeTarget(ep, {
          seriesId: id,
          seriesTitle: displayTitle,
          seriesPoster: displayPoster,
          ...(resume > 0 ? { resumeFrom: resume } : {}),
        }),
      )
    },
    [onPlay, displayTitle, displayPoster, id],
  )

  // Botão principal da série: abre o episódio do "continuar" (retoma de onde
  // parou ou começa o próximo/1º), não sempre o 1º da temporada ativa.
  const playContinue = useCallback(() => {
    if (!seriesContinue) return
    playEpisode(seriesContinue.episode, seriesContinue.positionSec)
  }, [seriesContinue, playEpisode])

  // ===== Estados de borda ===== //
  if (isNoProvider(vodQ.error) || isNoProvider(seriesQ.error)) {
    return <MobileNoSource onConfigureSource={onConfigureSource} />
  }
  const loading = kind === 'vod' ? vodQ.isLoading : seriesQ.isLoading
  // Sem nada do card pra pintar e ainda carregando → spinner.
  if (loading && !displayTitle) {
    return <MobileLoading />
  }

  const canPlayVod = kind === 'vod'
  // Episódio que o botão principal abre. Sem progresso cai no 1º do catálogo;
  // com progresso, retoma/avança (pickSeriesContinue). Fallback defensivo no 1º
  // da temporada ativa caso allEpisodes ainda não tenha resolvido.
  const ctaEpisode = seriesContinue?.episode ?? episodes[0]
  // Rótulo do botão: retomar/avançar (mostra T·E) vs iniciar (genérico).
  const seriesCtaLabel =
    seriesContinue && seriesContinue.mode !== 'start' && ctaEpisode
      ? t('series.continueEpisode', {
          season: ctaEpisode.seasonNumber,
          episode: ctaEpisode.episodeNumber,
        })
      : t('series.playEpisode')

  return (
    <div className={styles.detail} data-screen="detail" data-kind={kind}>
      {/* ===== Cabeçalho: backdrop + poster + título/meta ===== */}
      <div className={styles.header}>
        <div
          className={styles.backdrop}
          style={{
            backgroundImage: backdrop
              ? cssUrl(rewriteImgUrl(backdrop, { kind: 'backdrop' }))
              : undefined,
          }}
          aria-hidden="true"
        />
        <div className={styles.scrim} aria-hidden="true" />
        <div className={styles.headerInner}>
          <div className={styles.posterWrap}>
            <Poster
              src={displayPoster}
              kind="poster-large"
              alt={displayTitle}
              fallbackText={displayTitle}
              className={styles.poster}
              placeholderClassName={`${styles.poster} ${styles.posterPh}`}
            />
          </div>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>{displayTitle}</h1>
            <div className={styles.metaRow}>
              {year != null && <span className={styles.metaItem}>{year}</span>}
              {genre && <span className={styles.metaItem}>{genre}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Ações ===== */}
      <div className={styles.actions}>
        {canPlayVod ? (
          <button type="button" className={styles.playBtn} onClick={playVod}>
            ▶{' '}
            {resumeFrom > 0
              ? t('movies.details.resume', { time: formatClock(resumeFrom) })
              : t('movies.details.play')}
          </button>
        ) : (
          <button
            type="button"
            className={styles.playBtn}
            data-cta-mode={seriesContinue?.mode ?? 'start'}
            onClick={playContinue}
            disabled={!ctaEpisode}
          >
            ▶ {seriesCtaLabel}
          </button>
        )}
        <button
          type="button"
          className={styles.favBtn}
          data-active={favorited ? 'true' : 'false'}
          aria-pressed={favorited ? 'true' : 'false'}
          aria-label={favorited ? t('movies.details.unfavorite') : t('movies.details.favorite')}
          onClick={toggleFavorite}
        >
          {favorited ? '♥' : '♡'}
        </button>
      </div>

      {/* ===== Sinopse ===== */}
      {plot && <p className={styles.plot}>{plot}</p>}

      {/* ===== Série: temporadas + episódios ===== */}
      {kind === 'series' && seasonKeys.length > 0 && (
        <div className={styles.seasons} data-section="seasons">
          {seasonKeys.length > 1 && (
            <div className={styles.seasonChips} role="tablist" aria-label={t('series.selectEpisode')}>
              {seasonKeys.map((k) => {
                const active = k === activeSeason
                return (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    className={styles.seasonChip}
                    data-season={k}
                    data-active={active ? 'true' : 'false'}
                    aria-selected={active ? 'true' : 'false'}
                    onClick={() => setPickedSeason(k)}
                  >
                    {t('series.season', { n: k })}
                  </button>
                )
              })}
            </div>
          )}

          <ul className={styles.episodeList} data-list="episodes">
            {episodes.map((ep) => {
              const prog = epProgress[ep.episodeId]
              const watched = prog?.finished === true
              const inProgress =
                !!prog && !prog.finished && prog.positionSec > 0 && prog.durationSec > 0
              const pct = inProgress
                ? Math.min(100, Math.round((prog.positionSec / prog.durationSec) * 100))
                : 0
              // Tap direto no episódio retoma de onde parou (se em andamento).
              const resume = inProgress ? Math.floor(prog.positionSec) : 0
              const isCta = seriesContinue?.episode.episodeId === ep.episodeId
              return (
              <li key={ep.episodeId}>
                <button
                  type="button"
                  className={styles.episodeRow}
                  data-episode={ep.episodeId}
                  data-watched={watched ? 'true' : 'false'}
                  data-progress={inProgress ? 'true' : 'false'}
                  data-current={isCta ? 'true' : 'false'}
                  onClick={() => playEpisode(ep, resume)}
                >
                  <span className={styles.episodeThumb}>
                    <Poster
                      src={ep.poster}
                      kind="thumb"
                      alt={ep.title}
                      fallbackText={ep.title}
                      fallbackSrc={displayPoster}
                      className={styles.episodeThumbImg}
                    />
                    {watched && (
                      <span className={styles.episodeWatched} aria-hidden="true">
                        ✓
                      </span>
                    )}
                    {inProgress && (
                      <span className={styles.episodeProgress} aria-hidden="true">
                        <span className={styles.episodeProgressFill} style={{ width: `${pct}%` }} />
                      </span>
                    )}
                  </span>
                  <span className={styles.episodeInfo}>
                    <span className={styles.episodeNum}>
                      {t('series.episode', { n: ep.episodeNumber })}
                      {watched && (
                        <span className={styles.episodeBadge}>{t('series.watched')}</span>
                      )}
                    </span>
                    <span className={styles.episodeTitle}>{ep.title}</span>
                  </span>
                  <span className={styles.episodePlay} aria-hidden="true">
                    ▶
                  </span>
                </button>
              </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

/** Formata segundos como mm:ss / h:mm:ss (pra "Continuar de {time}"). */
function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '00:00'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
